import { resolveSectors } from "@/data/compute-sectors";
import type { Holding, Sector } from "@/data/fund-detail-types";
import { useState } from "react";

/**
 * Sector breakdown visualization. Ported from the dane etf-website
 * `components/fund-sectors.tsx`.
 */
export function FundSectors({
  sectors: sectorsProp = [],
  holdings = [],
  holdingsCount,
  topTenPct,
  isUnderlying,
  underlyingSecurityType = "ETF",
  sectorLabel,
  tickLabel,
  sectorCountLabel,
  sectorFootnote,
  sectorLayout = "grid",
}: {
  sectors?: Sector[];
  holdings?: Holding[];
  holdingsCount: number;
  topTenPct?: number;
  isUnderlying?: boolean;
  underlyingSecurityType?: "ETF" | "common stock";
  sectorLabel?: string;
  tickLabel?: string;
  sectorCountLabel?: string;
  sectorFootnote?: string;
  sectorLayout?: "grid" | "flex";
}) {
  const sectors = resolveSectors(sectorsProp, holdings);

  if (sectors.length === 0) return null;

  const sectorCount = sectors.length;
  // A fund is "single-asset" only when it holds ≤1 position AND we're showing
  // the fund's OWN holdings. When `isUnderlying` is set (leveraged-2x showing
  // the underlying ETF's sector breakdown), the breakdown is multi-sector even
  // though the lev fund itself holds no underlying-company feed (holdingsCount
  // arrives as 0) — so it must read "Sector Breakdown (Underlying ETF)", not
  // "Asset Breakdown (Underlying ETP)".
  const isSingleAsset = !isUnderlying && holdingsCount <= 1;
  const headerLabel =
    sectorLabel ??
    (isSingleAsset
      ? "Asset Breakdown (Underlying ETP)"
      : isUnderlying
        ? "Sector Breakdown (Underlying ETF)"
        : "Sector Breakdown\u00B9");

  return (
    <div
      data-fund-section="sectors sectorLabel sectorTickLabel sectorCountLabel sectorFootnote sectorLayout founders"
      className="px-10 pt-6 pb-5 max-md:px-4 max-md:pt-5 max-md:pb-4"
    >
      <div className="flex items-center justify-between gap-3 mb-[22px] max-md:flex-col max-md:items-start max-md:gap-1.5 max-md:mb-4">
        <div className="font-[family-name:var(--font-instrument)] text-[12px] leading-[1.5] tracking-[0.12em] text-[var(--etf-white3)] uppercase">
          {headerLabel}
        </div>
        <div className="font-[family-name:var(--font-instrument)] text-[12px] leading-[1.5] text-[var(--etf-white)]">
          {/* Only show the company count when we actually have one (>0). The
              leveraged underlying-ETF NAICS breakdown has no live company
              feed → holdingsCount is 0; show just the sector count, never
              a fabricated "0 companies". */}
          {!isSingleAsset && holdingsCount > 0 && <>{holdingsCount} companies &middot; </>}
          <span className="text-[var(--etf-orange)]">
            {sectorCountLabel ?? `${sectorCount} ${sectorCount === 1 ? "category" : "sectors"}`}
          </span>
        </div>
      </div>

      <div
        className={
          sectorLayout === "grid"
            ? "grid grid-cols-[repeat(auto-fill,minmax(148px,1fr))] gap-x-6 gap-y-5 mb-7 max-md:gap-x-3 max-md:gap-y-3 max-md:mb-5 max-[480px]:gap-x-2 max-[480px]:gap-y-2.5"
            : "flex flex-wrap gap-x-7 gap-y-5 mb-7 max-md:gap-x-3 max-md:gap-y-3 max-md:mb-5 max-[480px]:gap-x-2 max-[480px]:gap-y-2.5"
        }
      >
        {sectors.map((sector) => (
          <DotCategory
            key={sector.name}
            sector={sector}
            hideCounts={isSingleAsset}
            sectorLayout={sectorLayout}
          />
        ))}
      </div>

      <SectorBar
        sectors={sectors}
        topTenPct={isSingleAsset ? undefined : topTenPct}
        isSingleAsset={isSingleAsset}
        tickLabel={tickLabel}
      />

      {!isSingleAsset && (
        <p className="font-[family-name:var(--font-instrument)] text-[10px] text-[var(--etf-white3)] tracking-[0.04em] mt-3">
          &sup1;{" "}
          {sectorFootnote ??
            `Sector Breakdown reflects the percentage of the ${
              isUnderlying ? `underlying ${underlyingSecurityType}\u2019s` : "fund\u2019s"
            } portfolio allocated to each market sector based on the fund\u2019s current holdings, as of the date shown. Sector classifications are based on the North American Industry Classification System (NAICS)`}
        </p>
      )}
    </div>
  );
}

function DotCategory({
  sector,
  hideCounts,
  sectorLayout,
}: {
  sector: Sector;
  hideCounts?: boolean;
  sectorLayout: "grid" | "flex";
}) {
  const dotCount = Math.max(sector.companies, 0);
  const cellWidthClasses =
    sectorLayout === "grid" ? "max-w-none" : "max-w-[120px] max-md:max-w-[88px]";
  return (
    <div className={`flex flex-col gap-1.5 max-md:gap-1 min-w-0 pt-[18px] ${cellWidthClasses}`}>
      <div className="relative flex flex-wrap-reverse content-start gap-1 w-14 h-9 max-md:w-12 max-md:h-7 max-md:gap-[3px]">
        <div className="absolute top-[-16px] left-0 right-0 text-center font-[family-name:var(--font-instrument)] text-[11px] font-semibold text-[var(--etf-white)] tracking-[0.02em] leading-none">
          {sector.pct}%
        </div>
        {Array.from({ length: dotCount }).map((_, i) => (
          <span
            key={`${sector.name}-dot-${i}`}
            className="w-1.5 h-1.5 rounded-full max-md:w-1 max-md:h-1"
            style={{
              background: sector.color,
              boxShadow: `0 0 4px ${sector.color}4D`,
            }}
          />
        ))}
      </div>
      <div className="font-[family-name:var(--font-instrument)] text-[11px] max-md:text-[10px] text-[var(--etf-white3)] tracking-[0.04em] leading-[1.35]">
        {truncateSectorName(sector.name)}
      </div>
      {!hideCounts && (
        <div className="font-[family-name:var(--font-instrument)] text-[10px] max-md:text-[9px] text-[var(--etf-white3)] opacity-60 leading-tight">
          {sector.description ?? `${sector.companies} companies`}
        </div>
      )}
    </div>
  );
}

function truncateSectorName(name: string): string {
  return name
    .replace("Information Technology", "Information")
    .replace("Cash & Equivalents", "Cash & Equiv.")
    .replace("Professional Services", "Prof. Services")
    .replace("Mining & Extraction", "Mining & Extract.")
    .replace("Consumer Discretionary", "Consumer Disc.")
    .replace("Communication Services", "Comm. Services");
}

function SectorBar({
  sectors,
  topTenPct,
  isSingleAsset,
  tickLabel,
}: { sectors: Sector[]; topTenPct?: number; isSingleAsset?: boolean; tickLabel?: string }) {
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);

  return (
    <div className="relative">
      <div className="flex h-7 rounded overflow-hidden bg-white/[0.02]">
        {sectors.map((sector, i) => (
          <div
            key={sector.name}
            className="relative overflow-hidden transition-[opacity,filter] duration-200 hover:brightness-125 before:content-[''] before:absolute before:inset-0 before:bg-[repeating-linear-gradient(90deg,var(--seg-color)_0px,var(--seg-color)_2px,transparent_2px,transparent_4.5px)] before:opacity-90 after:content-[''] after:absolute after:inset-0 after:bg-[linear-gradient(180deg,rgba(255,255,255,0.08)_0%,transparent_60%)]"
            style={
              {
                flex: sector.pct,
                "--seg-color": sector.color,
              } as React.CSSProperties
            }
            onMouseEnter={() => setHoveredIndex(i)}
            onMouseLeave={() => setHoveredIndex(null)}
          >
            {hoveredIndex === i && (
              <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2.5 pointer-events-none bg-[var(--etf-black2)] border border-white/[0.13] rounded-md px-3 py-1.5 whitespace-nowrap z-10 font-[family-name:var(--font-instrument)] text-[11px] max-md:text-[10px] max-md:px-2 max-md:py-1 text-[var(--etf-white)] shadow-[0_4px_20px_rgba(0,0,0,0.6)]">
                {sector.name} &middot;
                {!isSingleAsset && sector.companies > 0 && (
                  <> {sector.companies} companies &middot;</>
                )}{" "}
                {sector.pct}%
              </div>
            )}
          </div>
        ))}
      </div>

      <div className="relative h-4 mt-1.5">
        {[0, 25, 50, 75, 100].map((pct) => (
          <div
            key={pct}
            className="absolute top-0 flex flex-col items-center"
            style={{ left: `${pct}%`, transform: pct === 100 ? "translateX(-100%)" : undefined }}
          >
            <span className="w-px h-1.5 bg-white/[0.12]" />
            <span className="font-[family-name:var(--font-instrument)] text-[9px] text-[var(--etf-white3)] mt-0.5 tracking-[0.04em]">
              {pct}%
            </span>
          </div>
        ))}
        {topTenPct != null && (
          <div
            className="absolute top-0 flex flex-col items-center"
            style={{ left: `${topTenPct}%` }}
          >
            <span className="w-px h-1.5 bg-white/[0.12]" />
            <span className="font-[family-name:var(--font-instrument)] text-[9px] text-[var(--etf-orange)] font-semibold mt-0.5 tracking-[0.04em]">
              {tickLabel ?? "Top 10"}
            </span>
          </div>
        )}
      </div>
    </div>
  );
}
